import {
  ChakraBox,
  ChakraSimpleGrid,
  ChakraFlex,
  Icon,
  Text,
  UniversalSelect,
  FAIconName,
  TextInputV2,
  ChakraGridItem,
  FileIconsV2,
  FireflyButton,
  useToastMessage,
  ToastMessage,
  ChakraImage,
} from '@equip.health/ui';
import { useMemo, useState } from 'react';
import { GENERIC_API_FAILURE_MESSAGE } from '~/lib/constants/profile.constants';
import {
  DUPLICATE_FILE_NAME_ERROR_MESSAGE,
  INVALID_FILE_NAME_MESSAGE,
} from '~/lib/constants/documentCenter.constants';
import { getFileIconV2, isImage } from '~/lib/util/chat/chat.utils';
import fileUtil, { getFileExtension } from '~/lib/util/file.util';
import { useUpdateDocumentMutation } from '~/store/services/documents';
import { useGetPatientDocumentTagsQuery } from '~/store/services/enumerations';
import { buildUniversalSelectSearchTerm } from '~/lib/util/inquiry.util';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { DocumentCenterEvents } from '~/analytics/documentCenterEvents';
import { debounce } from 'lodash';

const templateColumns = '3fr .8fr 3.3fr 60px 20px';

interface EditDocumentProps {
  modalId: string;
  documentDetails: Partial<PatientDocumentV2>;
  onClose: () => void;
}

const EditDocument = ({
  modalId,
  documentDetails,
  onClose,
}: EditDocumentProps) => {
  const { trackEvent } = useAnalytics();
  const { data: documentTagsList } = useGetPatientDocumentTagsQuery();
  const [updateDocument, { isLoading: isUpdateDocumentLoading }] =
    useUpdateDocumentMutation();
  const getFormattedOptions = (
    options: DocumentTag[],
  ): UniversalSelectOption[] => {
    return (options ?? []).map((value) => ({
      label: value.label,
      value: value.code,
    }));
  };
  const [documentTags, setDocumentTags] = useState<UniversalSelectOption[]>(
    getFormattedOptions(documentDetails?.documentTags || []),
  );
  const [fileName, setFileName] = useState(
    fileUtil.getFileNameWithoutExtension(documentDetails?.fileName) || '',
  );
  const [sensitive, setSensitive] = useState(
    documentDetails?.sensitive || false,
  );

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fileExtension = getFileExtension(documentDetails?.fileName);
  const { createToast } = useToastMessage();
  const handleSave = async () => {
    const trimmedFileName = fileName.trim();

    if (!fileUtil.validateFileName(fileName)) {
      setErrorMsg(INVALID_FILE_NAME_MESSAGE);
      return;
    }

    const finalFileName = fileExtension
      ? `${trimmedFileName}.${fileExtension}`
      : trimmedFileName;

    trackEvent(DocumentCenterEvents.editModalSaveClicked, {
      Source: AnalyticsSourceValues.DOCUMENT_CENTER,
      DocumentExternalId: documentDetails?.documentExternalId,
      FileExtension: fileExtension,
      Tags: documentTags.map((tag) => tag.value),
      Sensitive: sensitive,
    });

    try {
      await updateDocument({
        body: {
          documentTags: documentTags.map((tag) => tag.value),
          fileName: finalFileName,
          sensitive,
        },
        documentExternalId: documentDetails?.documentExternalId,
      }).unwrap();

      onClose();
      createToast({
        icon: FAIconName.checkRegular,
        id: 'document-update-success_toast',
        text: 'Document properties updated',
        variant: 'success',
      });
    } catch (error) {
      const errorString =
        typeof error?.data === 'string'
          ? error.data
          : (JSON.stringify(error?.data) ?? '');

      const isDuplicateError = errorString.includes(
        'DUPLICATE_FILE_NAME_ERROR',
      );

      if (isDuplicateError) {
        setErrorMsg(DUPLICATE_FILE_NAME_ERROR_MESSAGE);
      } else {
        setErrorMsg(GENERIC_API_FAILURE_MESSAGE);
      }
    }
  };

  const debouncedTrackSearchEvent = useMemo(
    () =>
      debounce((inputValue: string) => {
        trackEvent(DocumentCenterEvents.documentTypeSearchPerformed, {
          Source: AnalyticsSourceValues.DOCUMENT_CENTER,
          SearchTerm: inputValue,
        });
      }, 500),
    [trackEvent],
  );

  return (
    <ChakraBox width="100%">
      <ChakraBox width="100%">
        <ChakraSimpleGrid
          alignItems="center"
          background="neutral.background.secondary"
          borderBottom="1px solid"
          borderColor="neutral.line"
          borderRadius="6px"
          color="neutral.text.secondary"
          columnGap="24px"
          padding="10px 12px"
          templateColumns={templateColumns}
        >
          <ChakraGridItem>
            <Text
              color="neutral.secondary"
              paddingLeft="52px"
              variant="caption"
            >
              Name{' '}
              <ChakraBox as="span" color="critical.base">
                *
              </ChakraBox>
            </Text>
          </ChakraGridItem>
          <ChakraGridItem>
            <Text color="neutral.secondary" variant="caption">
              File format
            </Text>
          </ChakraGridItem>
          <ChakraGridItem>
            <Text color="neutral.secondary" variant="caption">
              Document type
            </Text>
          </ChakraGridItem>
          <ChakraGridItem>
            <Text color="neutral.secondary" variant="caption">
              Sensitive?
            </Text>
          </ChakraGridItem>
        </ChakraSimpleGrid>

        <ChakraBox display="grid" gap="15px" padding="8px 12px 24px 12px">
          <ChakraSimpleGrid
            alignItems="center"
            columnGap="24px"
            key={documentDetails?.documentExternalId}
            rowGap="15px"
            templateColumns={templateColumns}
          >
            <ChakraGridItem>
              <ChakraFlex alignItems="center" gap="10px">
                {isImage(documentDetails?.fileName) &&
                (documentDetails?.thumbnailUrl ||
                  documentDetails?.downloadUrl) ? (
                  <ChakraImage
                    alt={fileName}
                    boxSize="40px"
                    fit="contain"
                    loading="lazy"
                    src={
                      documentDetails?.thumbnailUrl ||
                      documentDetails?.downloadUrl
                    }
                    title={fileName}
                  />
                ) : (
                  <FileIconsV2
                    height="40px"
                    icon={getFileIconV2(documentDetails?.fileName)}
                    width="40px"
                  />
                )}
                <TextInputV2
                  id={`${documentDetails?.documentExternalId}__document-name-input`}
                  isInvalid={fileName && !fileUtil.validateFileName(fileName)}
                  isReadOnly={!documentDetails?.uploadedViaFileService}
                  onChange={(value) => {
                    setErrorMsg(null);
                    setFileName(value);
                  }}
                  onFocus={() => {
                    trackEvent(DocumentCenterEvents.documentNameFieldClicked, {
                      Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                    });
                  }}
                  readonlyOverflowWrap="anywhere"
                  readonlyWhiteSpace="normal"
                  value={fileName}
                />
              </ChakraFlex>
            </ChakraGridItem>

            <ChakraGridItem>
              <Text color="neutral.secondary">
                {getFileExtension(documentDetails?.fileName)?.toUpperCase() ||
                  '—'}
              </Text>
            </ChakraGridItem>

            <ChakraGridItem>
              <UniversalSelect
                id={`${documentDetails?.documentExternalId}__document-type-select`}
                isSearchable
                isSingleSelect={false}
                leftIcon={FAIconName.magnifyingGlassRegular}
                modalId={modalId}
                onChange={(val) => {
                  if (!val || (Array.isArray(val) && val.length === 0)) {
                    trackEvent(DocumentCenterEvents.documentTypeCleared, {
                      Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                    });
                  } else {
                    trackEvent(DocumentCenterEvents.documentTypeOptionClicked, {
                      Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                      Option: val.map((s) => s.value).join(', '),
                    });
                  }
                  setDocumentTags(val);
                }}
                onInputChange={(inputValue) => {
                  debouncedTrackSearchEvent(inputValue);
                }}
                onMenuOpen={() => {
                  trackEvent(DocumentCenterEvents.documentTypeMenuClicked, {
                    Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                  });
                }}
                options={documentTagsList?.map((tag) => ({
                  ...tag,
                  searchData: buildUniversalSelectSearchTerm(tag.label),
                }))}
                placeholder="Select all that apply"
                value={documentTags}
              />
            </ChakraGridItem>

            <ChakraGridItem
              cursor="pointer"
              display="flex"
              justifyContent="center"
              onClick={() =>
                setSensitive((prev) => {
                  const nextSensitive = !prev;
                  trackEvent(
                    nextSensitive
                      ? DocumentCenterEvents.documentSensitiveFlagged
                      : DocumentCenterEvents.documentSensitiveUnflagged,
                    {
                      Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                    },
                  );
                  return nextSensitive;
                })
              }
            >
              {sensitive ? (
                <Icon
                  aria-label="Flag sensitive"
                  color="critical.base"
                  name={FAIconName.flagSolid}
                />
              ) : (
                <Icon
                  aria-label="Unflag sensitive"
                  color="neutral.tertiary"
                  name={FAIconName.flagRegular}
                />
              )}
            </ChakraGridItem>
          </ChakraSimpleGrid>
        </ChakraBox>
      </ChakraBox>
      {errorMsg && (
        <ChakraBox marginBottom="24px">
          <ToastMessage
            icon={FAIconName.exclamationTriangleRegular}
            id="error-msg"
            isFullWidth
            text={errorMsg}
            variant="critical"
          />
        </ChakraBox>
      )}
      <ChakraFlex justifyContent="space-between">
        <FireflyButton
          id="cancel"
          leftIcon={FAIconName.timesRegular}
          onClick={() => {
            trackEvent(DocumentCenterEvents.editModalCancelClicked, {
              Source: AnalyticsSourceValues.DOCUMENT_CENTER,
            });
            onClose();
          }}
          variant="secondary"
        >
          Cancel
        </FireflyButton>
        <FireflyButton
          id="save-document"
          isLoading={isUpdateDocumentLoading}
          leftIcon={FAIconName.checkRegular}
          onClick={handleSave}
          variant="primary"
        >
          Save changes
        </FireflyButton>
      </ChakraFlex>
    </ChakraBox>
  );
};

export default EditDocument;
